import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { AdminApi } from '../../services/api';
import { BOUTON_AUTH, CHAMP_AUTH, CadreAuth } from './CadreAuth';
import { LONGUEUR_MIN_MOT_DE_PASSE, verifierNouveauMotDePasse } from './motDePasseObligatoire';

/** Délai avant le retour automatique à la connexion après réussite. */
const RETOUR_CONNEXION_MS = 4000;

/**
 * Page /reinitialiser/:jeton, ouverte depuis le lien reçu par e-mail
 * (accessible sans être connecté). Pose le nouveau mot de passe puis renvoie
 * à la connexion : aucune connexion automatique.
 */
export const ReinitialiserMotDePasse: React.FC = () => {
  const { jeton = '' } = useParams<{ jeton: string }>();
  const navigate = useNavigate();
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [reussite, setReussite] = useState<string | null>(null);

  useEffect(() => {
    if (!reussite) return;
    const t = setTimeout(() => navigate('/login', { replace: true }), RETOUR_CONNEXION_MS);
    return () => clearTimeout(t);
  }, [reussite, navigate]);

  const envoyer = async (e: React.FormEvent) => {
    e.preventDefault();
    const probleme = verifierNouveauMotDePasse(motDePasse, confirmation);
    setErreur(probleme);
    if (probleme) return;
    setEnvoi(true);
    try {
      const message = await AdminApi.reinitialiserMotDePasse(jeton, motDePasse);
      setReussite(message);
      setMotDePasse(''); setConfirmation('');
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Ce lien a expiré ou a déjà servi.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CadreAuth titre="Nouveau mot de passe" sousTitre="Choisissez le mot de passe de votre compte.">
      {reussite ? (
        <div role="status" className="mt-8 p-4 bg-sapin-50 border border-sapin-200 rounded-lg flex items-start gap-3 text-sapin-900 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p>{reussite} Retour à la connexion dans quelques secondes.</p>
        </div>
      ) : (
        <>
          {erreur && (
            <div role="alert" className="mt-6 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" /><p>{erreur}</p>
            </div>
          )}
          <form onSubmit={envoyer} className="mt-8 space-y-5" noValidate>
            <div>
              <label htmlFor="reinit-mdp" className="block text-sm font-semibold text-encre mb-1.5">Nouveau mot de passe</label>
              <input id="reinit-mdp" type="password" value={motDePasse} onChange={e => setMotDePasse(e.target.value)} autoComplete="new-password" className={CHAMP_AUTH} />
              <p className="mt-1 text-xs text-graphite">Au moins {LONGUEUR_MIN_MOT_DE_PASSE} caractères.</p>
            </div>
            <div>
              <label htmlFor="reinit-confirmation" className="block text-sm font-semibold text-encre mb-1.5">Confirmation</label>
              <input id="reinit-confirmation" type="password" value={confirmation} onChange={e => setConfirmation(e.target.value)} autoComplete="new-password" className={CHAMP_AUTH} />
            </div>
            <button type="submit" disabled={envoi || !jeton} className={BOUTON_AUTH}>
              {envoi ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Enregistrement</span></> : <span>Enregistrer le mot de passe</span>}
            </button>
          </form>
        </>
      )}
      <Link to="/login" replace className="mt-8 inline-flex items-center min-h-[40px] lg:min-h-0 text-sm text-sapin-700 hover:text-sapin-900 font-semibold">Retour à la connexion</Link>
    </CadreAuth>
  );
};

export default ReinitialiserMotDePasse;
